import React, { useState } from 'react';
import { X, Copy, Check, ExternalLink, ShieldOff } from 'lucide-react';
import { zIndexClasses } from '../../../config/zIndex';

interface ChatShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  messageCount: number;
  viewCount?: number;
  forkCount?: number;
  onRevoke?: () => void;
  isRevoking?: boolean;
}

const ChatShareModal: React.FC<ChatShareModalProps> = ({
  isOpen,
  onClose,
  shareUrl,
  messageCount,
  viewCount = 0,
  forkCount = 0,
  onRevoke,
  isRevoking = false,
}) => {
  const [copied, setCopied] = useState(false);

  const fullUrl = `${window.location.origin}${shareUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 ${zIndexClasses.maximum}`}
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`fixed inset-0 flex items-center justify-center ${zIndexClasses.maximum} px-4`}>
        <div
          className="bg-white rounded-xl shadow-xl max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Share Chat
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Anyone with this link can view your chat ({messageCount} messages) and fork it to continue the conversation in their own account.
            </p>

            {/* Stats */}
            {(viewCount > 0 || forkCount > 0) && (
              <div className="flex gap-4 text-sm text-gray-500">
                {viewCount > 0 && (
                  <span>{viewCount} view{viewCount !== 1 ? 's' : ''}</span>
                )}
                {forkCount > 0 && (
                  <span>{forkCount} fork{forkCount !== 1 ? 's' : ''}</span>
                )}
              </div>
            )}

            {/* URL Display */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <input
                type="text"
                value={fullUrl}
                readOnly
                className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 font-mono"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                onClick={handleCopy}
                className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-primary" />
                ) : (
                  <Copy className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>

            {/* Success message */}
            {copied && (
              <div className="flex items-center gap-2 text-sm text-primary">
                <Check className="w-4 h-4" />
                <span>Link copied to clipboard!</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleCopy}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-medium"
              >
                Copy Link
              </button>
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-2 text-gray-700"
              >
                <ExternalLink className="w-4 h-4" />
                Open
              </a>
            </div>

            {/* Revoke Option */}
            {onRevoke && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-3">
                  Want to stop sharing? Revoking the link will prevent anyone from viewing or forking this chat.
                </p>
                <button
                  onClick={() => {
                    onRevoke();
                    onClose();
                  }}
                  disabled={isRevoking}
                  className="w-full px-4 py-2 border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <ShieldOff className="w-4 h-4" />
                  {isRevoking ? 'Revoking...' : 'Revoke Share Link'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatShareModal;
